import type { ReactElement } from 'react';
import { useMemo } from 'react';

import { useServiceNow } from '@buying/library';
import { useLDClient } from '@launchdarkly/react-sdk';
import Button from '@mui/material/Button';
import { useLocalStorage } from 'usehooks-ts';

import { useGlobalState } from '../../../../../global_state/src';

interface SupportResponse {
    number: string;
    sysId: string;
}

const RequestPermissionsBtn = (): ReactElement => {
    const ldClient = useLDClient();

    const [msalAuthenticationResult] = useGlobalState(
        'msalAuthenticationResult'
    );
    const [backendBranches] = useGlobalState('backendBranches');
    const [msalProfile] = useGlobalState('msalProfile');
    const [buyingProfile] = useGlobalState('buyingProfile');
    const [, dispatchSnackbar] = useGlobalState('snackbarQueue');
    const [ticketData, setTicketData] = useLocalStorage<
        SupportResponse | undefined
    >(`role-request-${msalProfile?.mail}`, undefined);

    const { loading, createTicket, openTicket } = useServiceNow({
        idToken: msalAuthenticationResult?.idToken,
        autoOpenTickets: true,
        assignmentGroup: 'buying',
        supportBffBranch: backendBranches['support-bff'],
    });

    const flags = ldClient.allFlags();

    const buttonText = useMemo(() => {
        if (loading) return 'Creating request...';
        if (ticketData?.sysId) return 'View request';
        return 'Request role';
    }, [ticketData?.sysId, loading]);

    const handleRequestRoles = (): void => {
        if (!msalProfile) return;

        const title = `Role request: Purchase assistant`;
        const serviceNowDescription = `${msalProfile.displayName} is requesting access to create/edit orders in BestOne. The role needed is "Purchase assistant" and is not critical if you're a buyer. Remember to double check the user before granting roles.`;

        createTicket(msalProfile.mail, title, serviceNowDescription, {
            buyingProfile,
            msalProfile,
            featureFlags: flags,
        })
            .then((res) => {
                setTicketData(res);
                if (res.sysId) {
                    openTicket(res.sysId);
                }
            })
            .catch((error: unknown) => {
                dispatchSnackbar({
                    type: 'add',
                    payload: {
                        title: 'Could not automatically create support ticket!',
                        message: error ? String(error) : 'Unknown error',
                        type: 'error',
                    },
                });
                console.error(error);
            });
    };

    const handleButtonClick = (): void => {
        if (ticketData?.sysId) {
            openTicket(ticketData.sysId);
        } else {
            handleRequestRoles();
        }
    };

    return (
        <Button
            key="request-roles-button"
            onClick={handleButtonClick}
        >
            {buttonText}
        </Button>
    );
};

export { RequestPermissionsBtn };
